import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Stack,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReceiptStore } from '@/store/receiptStore';
import { ImagePreview } from '@/components/ImagePreview';
import { ReceiptDataDisplay } from '@/components/ReceiptDataDisplay';
import { LoadingBackdrop } from '@/components/LoadingBackdrop';
import { ErrorAlert } from '@/components/ErrorAlert';
import { uploadReceipt, saveReceipt } from '@/api/receipt';
import ReplayIcon from '@mui/icons-material/Replay';

export default function ResultPage() {
  const navigate = useNavigate();
  const {
    imageUri,
    imageData,
    receiptData,
    originalReceiptData,
    isLoading,
    error,
    setReceiptData,
    setOriginalReceiptData,
    setLoading,
    setError,
    reset,
  } = useReceiptStore();
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('Reprocessing receipt...');

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

  const handleNewScan = () => {
    reset();
    navigate('/upload');
  };

  const hasReceiptEdits = () => {
    if (!originalReceiptData) return false;
    try {
      return JSON.stringify(receiptData) !== JSON.stringify(originalReceiptData);
    } catch {
      return true;
    }
  };

  const getReconciliation = () => {
    if (!receiptData) return { isReconciled: false, difference: 0, calculatedTotal: 0 };
    const itemsTotal = receiptData.items.reduce((sum, item) => sum + item.price, 0);
    const discountsTotal = receiptData.saleItems.reduce((sum, sale) => sum + Math.abs(sale.discount), 0);
    const calculatedTotal = itemsTotal - discountsTotal + receiptData.tax;
    const difference = calculatedTotal - receiptData.total;
    // Compare as decimal strings to avoid floating point precision issues
    const isReconciled = calculatedTotal.toFixed(2) === receiptData.total.toFixed(2);
    return { isReconciled, difference, calculatedTotal };
  };

  const reconciliation = getReconciliation();

  // Clear errors when reconciliation is corrected
  useEffect(() => {
    if (reconciliation.isReconciled && error) {
      setError(null);
    }
  }, [reconciliation.isReconciled]);

  const handleRetry = async () => {
    if (!imageData) {
      setError('No original image data available to retry');
      return;
    }

    if (!hasReceiptEdits() && reconciliation.isReconciled) {
      const shouldRetry = window.confirm(
        'No edits detected and totals match. Do you still want to reprocess?'
      );
      if (!shouldRetry) return;
    }

    try {
      setLoadingMessage('Reprocessing receipt...');
      setLoading(true);
      setError(null);
      setSaveSuccess(null);

      if (originalReceiptData) {
        setReceiptData(originalReceiptData);
      }

      const response = await uploadReceipt({
        imageData,
      });

      setReceiptData(response);
      setOriginalReceiptData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReceipt = async () => {
    if (!receiptData) {
      setError('No receipt data available to save');
      return;
    }

    if (!receiptData.receiptId?.trim()) {
      setError('Missing receipt ID for save');
      return;
    }

    try {
      setLoadingMessage('Saving receipt...');
      setLoading(true);
      setError(null);
      setSaveSuccess(null);

      await saveReceipt({
        receiptId: receiptData.receiptId,
        store: receiptData.store,
        date: receiptData.date,
        payment: receiptData.payment,
        tax: receiptData.tax,
        total: receiptData.total,
        items: receiptData.items,
        saleItems: receiptData.saleItems,
      });

      setSaveSuccess('Receipt saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (!imageUri || !receiptData) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            gap: 2,
          }}
        >
          <Typography variant="h6">No receipt data available</Typography>
          <Button variant="contained" onClick={handleNewScan}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {/* Left Panel: Original Image */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: '100%', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ width: 40 }} />
                <Typography variant="h5" fontWeight="bold" sx={{ flex: 1, textAlign: 'center', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                  Original Receipt
                </Typography>
                <Tooltip title="Reprocess receipt image.">
                  <span>
                    <IconButton
                      onClick={handleRetry}
                      disabled={isLoading}
                      aria-label="retry receipt processing"
                    >
                      <ReplayIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <ImagePreview uri={imageUri} />
            </Paper>
          </Box>

          {/* Right Panel: Parsed Data */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: '100%', overflow: 'hidden' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold" align="center" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Parsed Data
              </Typography>
              <ReceiptDataDisplay data={receiptData} />
            </Paper>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <ErrorAlert message={error} onDismiss={() => setError(null)} />
        </Box>
        {saveSuccess && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" onClose={() => setSaveSuccess(null)}>
              {saveSuccess}
            </Alert>
          </Box>
        )}

        {/* Reconciliation Warning */}
        {!reconciliation.isReconciled && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">
              Cannot save: Items total minus discounts plus tax ({formatCurrency(reconciliation.calculatedTotal)}) does not equal receipt total ({formatCurrency(receiptData.total)}). Difference: {formatCurrency(reconciliation.difference)}
            </Alert>
          </Box>
        )}

        {/* Footer Actions */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleSaveReceipt}
              disabled={isLoading || !reconciliation.isReconciled}
            >
              Save Receipt
            </Button>
            <Button variant="contained" size="large" onClick={handleNewScan}>
              Scan Another Receipt
            </Button>
          </Stack>
        </Box>

      <LoadingBackdrop open={isLoading} message={loadingMessage} />
    </Container>
  );
}
